// Global Instructions Rule Applied!
//
// Configuration → Pipeline Budget (admin only; owner 2026-11-03).
//
// The GL accounts the Deal Pipeline → Budget tab lists under Cash
// (cfg.deal_pipeline_budget_accounts). Each line shows the account's ledger
// balance to date, debit-positive, and Net Cash is their sum - so a payable
// the fund owes deducts and one in debit (the fund is owed money) adds. Add an
// account to show it on the tab; remove it to take it out of Net Cash.
//
// A write refreshes this panel's own read only: the Budget read reads the
// table live, nothing in globalConfigs holds it, so the tab picks a change up
// on its next refresh.
//
// Chrome copied from StatementTypesPanel.tsx (FormSection + antd Table inside
// it, per-field autosave, the inline refusal Alert and a reload after every
// write) with the add / delete grammar of AccountOwnerRolesPanel.tsx (primary
// New button, AntD Modal with the default footer, Popconfirm on the row's
// danger delete icon).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Input, Modal, Popconfirm, Select, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import InlineTextField from './InlineTextField.tsx'
import InlineNumberField from './InlineNumberField.tsx'
import type {
  PipelineBudgetAccountAdminRow,
  PipelineBudgetGlAccountOption
} from '../../../state_manager/data_models/pipeline_budget_accounts_config_model.ts'
import {
  deletePipelineBudgetAccount,
  fetchPipelineBudgetAccountsConfig,
  PipelineBudgetAccountFields,
  savePipelineBudgetAccount
} from '../services/pipelineBudgetAccountsConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const LABEL_MAX = 60
const LABEL_HELP = 'The line\'s name on the Budget tab. Leave it blank to show the GL account\'s own name.'
const SIGN_HELP =
  'Each line is the account\'s ledger balance to date, debit-positive, and Net Cash is their sum: cash in the ' +
  'bank adds, a payable the fund owes deducts, and a payable in debit (the fund is owed money) adds.'

export default function PipelineBudgetAccountsPanel(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [rows, setRows] = useState<PipelineBudgetAccountAdminRow[]>([])
  const [glOptions, setGlOptions] = useState<PipelineBudgetGlAccountOption[]>([])
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)
  // Bumped on every load and part of the inline fields' keys, so a refused
  // edit snaps back to the stored value even when the value did not change.
  const [loadVersion, setLoadVersion] = useState(0)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // New account modal
  const [open, setOpen] = useState(false)
  const [newGlId, setNewGlId] = useState<string | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const read = await fetchPipelineBudgetAccountsConfig(token())
      setRows(read.data)
      setGlOptions(read.gl_account_options)
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Failed to load the pipeline budget accounts'
      setRefusal(text)
      showErrorToast(text)
    } finally {
      setLoadVersion((n) => n + 1)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const activateRef = useRefreshOnActivate(load)

  /** Every write goes through here: clear the last refusal, act, reload. On
   *  failure keep the message visible AND reload, so a control the server
   *  refused snaps back to the stored value. */
  const run = async (action: () => Promise<void>, failure: string): Promise<boolean> => {
    setRefusal(null)
    try {
      await action()
      await load()
      return true
    } catch (e) {
      const text = e instanceof Error ? e.message : failure
      setRefusal(text)
      showErrorToast(text)
      await load()
      return false
    }
  }

  const saveField = (row: PipelineBudgetAccountAdminRow, fields: PipelineBudgetAccountFields, failure: string): Promise<boolean> =>
    run(() => savePipelineBudgetAccount(token(), row.deal_pipeline_budget_accounts__id, fields), failure)

  // a GL account already on the tab cannot be picked twice
  const usedGlIds = useMemo(() => new Set(rows.map((r) => r.deal_pipeline_budget_accounts__gl_account_id)), [rows])
  const optionsFor = (keep: string | null): { value: string; label: string; disabled: boolean }[] =>
    glOptions.map((o) => ({ ...o, disabled: usedGlIds.has(o.value) && o.value !== keep }))

  const openAdd = (): void => {
    setNewGlId(null)
    setNewLabel('')
    setOpen(true)
  }

  const submitAdd = async (): Promise<void> => {
    if (!newGlId) return
    setSubmitting(true)
    const added = await run(
      () => savePipelineBudgetAccount(token(), null, { gl_account_id: newGlId, label: newLabel.trim() || null }),
      'Failed to add the budget account'
    )
    setSubmitting(false)
    if (added) {
      toast.success('Account added to the Budget tab')
      setOpen(false)
    }
  }

  const onDelete = async (row: PipelineBudgetAccountAdminRow): Promise<void> => {
    setDeletingId(row.deal_pipeline_budget_accounts__id)
    const removed = await run(
      () => deletePipelineBudgetAccount(token(), row.deal_pipeline_budget_accounts__id),
      'Failed to remove the budget account'
    )
    setDeletingId(null)
    if (removed) toast.success('Account removed from the Budget tab')
  }

  const columns: ColumnsType<PipelineBudgetAccountAdminRow> = [
    {
      title: 'GL account',
      dataIndex: 'deal_pipeline_budget_accounts__gl_account_id',
      width: 300,
      render: (v: string, row) => (
        <Select
          size='small'
          showSearch
          optionFilterProp='label'
          aria-label={`GL account of ${row.acc_gl_accounts__account_name}`}
          style={{ width: 280 }}
          popupMatchSelectWidth={false}
          value={v}
          options={optionsFor(v)}
          onChange={(next: string) => saveField(row, { gl_account_id: next }, 'Failed to change the GL account')}
        />
      )
    },
    {
      title: 'Type',
      dataIndex: 'acc_account_types__label',
      width: 110,
      render: (v: string | null) => v ?? <Text type='secondary'>—</Text>
    },
    {
      title: (
        <span>
          Label on the Budget tab
          <HelpTip title={LABEL_HELP} />
        </span>
      ),
      dataIndex: 'deal_pipeline_budget_accounts__label',
      width: 240,
      render: (v: string | null, row) => (
        <InlineTextField
          key={`${row.deal_pipeline_budget_accounts__id}-label-${loadVersion}`}
          value={v ?? ''}
          maxLength={LABEL_MAX}
          placeholder={row.acc_gl_accounts__account_name}
          onCommit={(next) => saveField(row, { label: next.trim() || null }, 'Failed to update the label')}
        />
      )
    },
    {
      title: 'Display order',
      dataIndex: 'deal_pipeline_budget_accounts__display_order',
      width: 120,
      render: (v: number, row) => (
        <InlineNumberField
          key={`${row.deal_pipeline_budget_accounts__id}-order-${loadVersion}`}
          value={v}
          min={0}
          max={10000}
          onCommit={(next) =>
            next === null ? load() : saveField(row, { display_order: next }, 'Failed to update the display order')
          }
        />
      )
    },
    {
      title: 'Last changed',
      dataIndex: 'deal_pipeline_budget_accounts__date_modified',
      width: 170,
      render: (v: string | null, row) => (
        <Text type='secondary' style={{ fontSize: 12 }}>
          {v ? `${row.prog__modified_by_name} · ${dayjs(v).format('MM/DD/YYYY')}` : row.prog__modified_by_name}
        </Text>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'right',
      render: (_: unknown, row) => (
        <Popconfirm
          title='Remove this account from the Budget tab?'
          description='It leaves the Cash lines and Net Cash. The GL account and its journals are not touched.'
          okText='Remove'
          onConfirm={() => onDelete(row)}
        >
          <Button
            type='text'
            danger
            size='small'
            aria-label={`Remove ${row.acc_gl_accounts__account_name}`}
            icon={<DeleteOutlined />}
            loading={deletingId === row.deal_pipeline_budget_accounts__id}
          />
        </Popconfirm>
      )
    }
  ]

  return (
    <div ref={activateRef} className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {refusal && <Alert type='error' showIcon closable message={refusal} onClose={() => setRefusal(null)} />}

      <FormSection
        title='Budget tab cash accounts'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <Space size='small'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              The GL accounts Deal Pipeline → Budget lists under Cash and sums into Net Cash
              <HelpTip title={SIGN_HELP} />
            </Text>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={openAdd}>
              New Account
            </Button>
          </Space>
        }
      >
        <Table
          size='small'
          rowKey='deal_pipeline_budget_accounts__id'
          pagination={false}
          dataSource={rows}
          columns={columns}
          locale={{ emptyText: 'No accounts - the Budget tab shows no cash lines and a Net Cash of zero.' }}
        />
      </FormSection>

      <Modal
        title='New account'
        open={open}
        okText='Add'
        okButtonProps={{ disabled: !newGlId }}
        confirmLoading={submitting}
        onOk={submitAdd}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <div className='flex flex-col gap-3 pt-2'>
          <div>
            <div className='text-xs text-gray-600 mb-1'>GL account</div>
            <Select
              showSearch
              optionFilterProp='label'
              aria-label='GL account to add'
              style={{ width: '100%' }}
              placeholder='Pick a GL account'
              value={newGlId ?? undefined}
              options={optionsFor(null)}
              onChange={(v: string) => setNewGlId(v)}
            />
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>
              Label on the Budget tab
              <HelpTip title={LABEL_HELP} />
            </div>
            <Input
              value={newLabel}
              maxLength={LABEL_MAX}
              placeholder='Blank = the GL account name'
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
